import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import LoginRequiredDialog from "@/components/LoginRequiredDialog";
import { useLoginGuard } from "@/hooks/useLoginGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Heart,
  MessageCircle,
  Share2,
  ShoppingBag,
  Volume2,
  VolumeX,
  Play,
  Pause,
  Home,
  Search,
  PlusSquare,
  User,
  ExternalLink,
  ChevronUp,
  Instagram,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const ReelsPage = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  const { requireLogin, dialogOpen, setDialogOpen, dialogMessage } = useLoginGuard();
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [likedReels, setLikedReels] = useState({});
  const containerRef = useRef(null);
  const videoRefs = useRef({});

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    try {
      const res = await axios.get(`${API}/reels?limit=50`);
      setReels(res.data.reels || []);
      
      // Check liked status for each reel if logged in
      if (token) {
        const likedStatus = {};
        for (const reel of res.data.reels || []) {
          try {
            const likeRes = await axios.get(`${API}/reels/${reel.id}/liked`, {
              headers: getAuthHeaders(),
            });
            likedStatus[reel.id] = likeRes.data.liked;
          } catch (e) {
            likedStatus[reel.id] = false;
          }
        }
        setLikedReels(likedStatus);
      }
    } catch (error) {
      console.error("Failed to fetch reels:", error);
    }
    setLoading(false);
  };

  const handleScroll = useCallback((e) => {
    const container = containerRef.current;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const itemHeight = window.innerHeight;
    const newIndex = Math.round(scrollTop / itemHeight);

    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < reels.length) {
      setCurrentIndex(newIndex);
      
      // Record view
      axios.post(`${API}/reels/${reels[newIndex].id}/view`).catch(() => {});
      
      // Pause previous video, play current
      Object.keys(videoRefs.current).forEach((key) => {
        const video = videoRefs.current[key];
        if (video) {
          if (parseInt(key) === newIndex) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      });
    }
  }, [currentIndex, reels]);

  const handleLike = async (reelId) => {
    if (!user) {
      requireLogin(null, "Please login to like reels and save your favorites.");
      return;
    }

    try {
      const res = await axios.post(`${API}/reels/${reelId}/like`, {}, {
        headers: getAuthHeaders(),
      });
      setLikedReels({ ...likedReels, [reelId]: res.data.liked });
      
      // Update local reel likes count
      setReels(reels.map(r => 
        r.id === reelId 
          ? { ...r, likes_count: r.likes_count + (res.data.liked ? 1 : -1) }
          : r
      ));
    } catch (error) {
      toast.error("Failed to like reel");
    }
  };

  const handleShare = (reel) => {
    const url = `${window.location.origin}/reels?id=${reel.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  const handleBuy = (reel) => {
    if (reel.product) {
      if (reel.product_type === "affiliate") {
        navigate(`/product/${reel.product_id}`);
      } else {
        // For seller products, open Instagram link
        window.open(reel.instagram_url, "_blank");
      }
    } else {
      window.open(reel.instagram_url, "_blank");
    }
  };

  const scrollToNext = () => {
    if (currentIndex < reels.length - 1) {
      containerRef.current?.scrollTo({
        top: (currentIndex + 1) * window.innerHeight,
        behavior: "smooth",
      });
    }
  };

  const scrollToPrev = () => {
    if (currentIndex > 0) {
      containerRef.current?.scrollTo({
        top: (currentIndex - 1) * window.innerHeight,
        behavior: "smooth",
      });
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="h-screen bg-black flex flex-col items-center justify-center text-white p-4">
        <Instagram className="w-20 h-20 mb-4 text-gray-500" />
        <h2 className="text-2xl font-bold mb-2">No Reels Yet</h2>
        <p className="text-gray-400 text-center mb-6">
          Check back soon for product videos and deals!
        </p>
        <Link to="/">
          <Button className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400">
            Browse Products
          </Button>
        </Link>
        
        {/* Bottom Navigation */}
        <BottomNav currentPage="reels" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-black overflow-hidden relative">
      <LoginRequiredDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        message={dialogMessage}
      />
      {/* Reels Container */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: "y mandatory" }}
      >
        {reels.map((reel, index) => (
          <ReelItem
            key={reel.id}
            reel={reel}
            index={index}
            isActive={index === currentIndex}
            muted={muted}
            setMuted={setMuted}
            liked={likedReels[reel.id]}
            onLike={() => handleLike(reel.id)}
            onShare={() => handleShare(reel)}
            onBuy={() => handleBuy(reel)}
            videoRef={(el) => (videoRefs.current[index] = el)}
          />
        ))}
      </div>

      {/* Top Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/60 to-transparent">
        <div className="flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="M Ventures" className="h-8" />
          </Link>
          <div className="flex items-center gap-2">
            <Badge className="bg-white/20 text-white backdrop-blur-sm">
              Reels
            </Badge>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      {currentIndex > 0 && (
        <button
          onClick={scrollToPrev}
          className="absolute top-20 left-1/2 -translate-x-1/2 z-20 text-white/60 animate-bounce"
        >
          <ChevronUp className="w-8 h-8" />
        </button>
      )}

      {/* Bottom Navigation */}
      <BottomNav currentPage="reels" user={user} />
    </div>
  );
};

const ReelItem = ({
  reel,
  index,
  isActive,
  muted,
  setMuted,
  liked,
  onLike,
  onShare,
  onBuy,
  videoRef,
}) => {
  const [playing, setPlaying] = useState(isActive);
  const localVideoRef = useRef(null);

  useEffect(() => {
    if (localVideoRef.current) {
      if (isActive) {
        localVideoRef.current.play().catch(() => {});
        setPlaying(true);
      } else {
        localVideoRef.current.pause();
        setPlaying(false);
      }
    }
  }, [isActive]);

  const togglePlay = () => {
    if (localVideoRef.current) {
      if (playing) {
        localVideoRef.current.pause();
      } else {
        localVideoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  const setRef = (el) => {
    localVideoRef.current = el;
    if (videoRef) videoRef(el);
  };

  // Determine video source - use video_url if available, otherwise try to embed Instagram
  const hasVideo = reel.video_url;
  const thumbnailUrl = reel.thumbnail_url || "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400";

  return (
    <div
      className="h-screen w-full snap-start relative flex items-center justify-center bg-black"
      style={{ scrollSnapAlign: "start" }}
    >
      {/* Video/Image Background */}
      {hasVideo ? (
        <video
          ref={setRef}
          src={reel.video_url}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted={muted}
          playsInline
          onClick={togglePlay}
        />
      ) : (
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{ backgroundImage: `url(${thumbnailUrl})` }}
          onClick={togglePlay}
        >
          <div className="absolute inset-0 bg-black/30" />
        </div>
      )}

      {/* Play/Pause Indicator */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
          <div className="w-20 h-20 bg-black/50 rounded-full flex items-center justify-center">
            <Play className="w-10 h-10 text-white ml-1" />
          </div>
        </div>
      )}

      {/* Right Side Actions */}
      <div className="absolute right-4 bottom-32 flex flex-col items-center gap-6 z-10">
        {/* Like */}
        <button
          onClick={onLike}
          className="flex flex-col items-center"
          data-testid={`like-reel-${reel.id}`}
        >
          <div className={`p-3 rounded-full ${liked ? 'bg-red-500' : 'bg-white/20 backdrop-blur-sm'}`}>
            <Heart className={`w-7 h-7 ${liked ? 'text-white fill-white' : 'text-white'}`} />
          </div>
          <span className="text-white text-sm mt-1">{reel.likes_count || 0}</span>
        </button>

        {/* Share */}
        <button onClick={onShare} className="flex flex-col items-center">
          <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
            <Share2 className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-sm mt-1">Share</span>
        </button>

        {/* Instagram Link */}
        <a
          href={reel.instagram_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center"
        >
          <div className="p-3 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500">
            <Instagram className="w-7 h-7 text-white" />
          </div>
          <span className="text-white text-sm mt-1">Insta</span>
        </a>

        {/* Mute Toggle */}
        <button onClick={() => setMuted(!muted)} className="flex flex-col items-center">
          <div className="p-3 rounded-full bg-white/20 backdrop-blur-sm">
            {muted ? (
              <VolumeX className="w-7 h-7 text-white" />
            ) : (
              <Volume2 className="w-7 h-7 text-white" />
            )}
          </div>
        </button>
      </div>

      {/* Bottom Content */}
      <div className="absolute bottom-24 left-0 right-0 p-4 z-10">
        {/* Seller Info */}
        {reel.seller && (
          <Link to={`/store/${reel.seller.store_slug}`} className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-bold">
                {reel.seller.business_name?.charAt(0)}
              </span>
            </div>
            <span className="text-white font-semibold">{reel.seller.business_name}</span>
          </Link>
        )}

        {/* Caption */}
        <p className="text-white text-sm mb-4 line-clamp-2">{reel.caption}</p>

        {/* Product Card */}
        {reel.product && (
          <div
            onClick={onBuy}
            className="bg-white/95 backdrop-blur-md rounded-2xl p-3 flex items-center gap-3 cursor-pointer active:scale-98 transition-transform"
            data-testid={`buy-product-${reel.id}`}
          >
            <img
              src={reel.product.image_url || reel.product.images?.[0]}
              alt={reel.product.title || reel.product.name}
              className="w-16 h-16 object-cover rounded-xl"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 truncate">
                {reel.product.title || reel.product.name}
              </p>
              <div className="flex items-center gap-2">
                <span className="font-bold text-primary-600">
                  ₹{reel.product.discounted_price || reel.product.price || reel.product.original_price}
                </span>
                {reel.product.cashback_percent && (
                  <Badge className="bg-primary-100 text-primary-700 text-xs">
                    {reel.product.cashback_percent}% Cashback
                  </Badge>
                )}
              </div>
            </div>
            <div className="bg-secondary-500 !text-primary-900 p-3 rounded-xl">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
          </div>
        )}

        {/* Buy Now Button (if no product linked) */}
        {!reel.product && (
          <Button
            onClick={onBuy}
            className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            View on Instagram
          </Button>
        )}
      </div>

      {/* Views Count */}
      <div className="absolute top-20 right-4 z-10">
        <Badge className="bg-black/50 text-white backdrop-blur-sm">
          {reel.views_count || 0} views
        </Badge>
      </div>
    </div>
  );
};

// Bottom Navigation Component
const BottomNav = ({ currentPage, user }) => {
  const navigate = useNavigate();

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 z-30 safe-area-pb">
      <div className="flex items-center justify-around py-2">
        <button
          onClick={() => navigate("/")}
          className={`flex flex-col items-center p-2 ${currentPage === "home" ? "text-white" : "text-gray-500"}`}
          data-testid="nav-home"
        >
          <Home className="w-6 h-6" />
          <span className="text-xs mt-1">Home</span>
        </button>

        <button
          onClick={() => navigate("/products")}
          className={`flex flex-col items-center p-2 ${currentPage === "search" ? "text-white" : "text-gray-500"}`}
          data-testid="nav-search"
        >
          <Search className="w-6 h-6" />
          <span className="text-xs mt-1">Shop</span>
        </button>

        <button
          onClick={() => navigate("/reels")}
          className={`flex flex-col items-center p-2 ${currentPage === "reels" ? "text-white" : "text-gray-500"}`}
          data-testid="nav-reels"
        >
          <div className={`p-1 rounded-lg ${currentPage === "reels" ? "bg-gradient-to-r from-purple-500 via-pink-500 to-amber-500" : ""}`}>
            <PlusSquare className="w-6 h-6" />
          </div>
          <span className="text-xs mt-1">Reels</span>
        </button>

        <button
          onClick={() => navigate(user ? "/dashboard" : "/login")}
          className={`flex flex-col items-center p-2 ${currentPage === "profile" ? "text-white" : "text-gray-500"}`}
          data-testid="nav-profile"
        >
          <User className="w-6 h-6" />
          <span className="text-xs mt-1">{user ? "Me" : "Login"}</span>
        </button>
      </div>
    </nav>
  );
};

export default ReelsPage;
